import {
  Card,
  ResourceList,
  ResourceItem,
  Thumbnail,
  Text,
  Button,
  Select,
  TextField,
  InlineStack,
  BlockStack,
  Box,
  Banner,
  Badge,
  Divider
} from '@shopify/polaris';
import { DeleteIcon, PrintIcon, ViewIcon } from '@shopify/polaris-icons';

/**
 * PrintQueue — shows queued variants (each item = variant row), print settings,
 * output format (PDF / ZPL), preview and the print action.
 */
function PrintQueue({
  queue,
  onRemove,
  onUpdateQuantity,
  onPrint,
  onPreview,
  subscription,
  presets,
  selectedPreset,
  onPresetChange,
  printFormat,
  onFormatChange,
  templates,
  selectedTemplate,
  onTemplateChange,
  printing
}) {
  const totalLabels = queue.reduce((sum, item) => sum + item.quantity, 0);
  const isBulk = totalLabels > 1;
  const needsSubscription = isBulk && !subscription?.active;

  const presetOptions = presets.map((preset) => ({
    label: preset.name,
    value: preset.id
  }));

  const templateOptions = [
    { label: 'Preset default layout', value: '' },
    ...(templates || []).map((t) => ({ label: `Template: ${t.name}`, value: t.id }))
  ];

  const selectedPresetInfo = presets.find((p) => p.id === selectedPreset);

  if (queue.length === 0) {
    return (
      <Box padding="800">
        <BlockStack gap="400" inlineAlign="center">
          <Text variant="headingMd">Print Queue Empty</Text>
          <Text variant="bodySm" tone="subdued">
            Select products from the Products tab to add them to your print queue.
          </Text>
        </BlockStack>
      </Box>
    );
  }

  return (
    <Box padding="400">
      <BlockStack gap="400">
        {/* Settings */}
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <Text variant="headingSm">Print Settings</Text>
              <InlineStack gap="400" align="space-between" blockEnd="center" wrap={false}>
                <BlockStack gap="200">
                  <Select
                    label="Label preset"
                    options={presetOptions}
                    value={selectedPreset}
                    onChange={onPresetChange}
                  />
                </BlockStack>
                <BlockStack gap="200">
                  <Select
                    label="Label template"
                    options={templateOptions}
                    value={selectedTemplate || ''}
                    onChange={onTemplateChange}
                  />
                </BlockStack>
                <BlockStack gap="200">
                  <Select
                    label="Output format"
                    options={[
                      { label: 'PDF (all printers)', value: 'pdf' },
                      { label: 'ZPL (Zebra thermal)', value: 'zpl' }
                    ]}
                    value={printFormat}
                    onChange={onFormatChange}
                  />
                </BlockStack>
                {selectedPresetInfo && (
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">
                      Label size
                    </Text>
                    <Text variant="bodyMd">
                      {(selectedPresetInfo.width / 72).toFixed(2)}" ×{' '}
                      {(selectedPresetInfo.height / 72).toFixed(2)}"
                    </Text>
                  </BlockStack>
                )}
              </InlineStack>
            </BlockStack>
          </Box>
        </Card>

        {/* Subscription Warning */}
        {needsSubscription && (
          <Banner
            title="Subscription required for bulk printing"
            tone="warning"
          >
            <p>
              You have {totalLabels} labels in your queue. Bulk printing (more than 1
              label) requires the $9/month plan. Your first single label is free.
            </p>
          </Banner>
        )}

        {/* Queue Items */}
        <Card>
          <ResourceList
            resourceName={{ singular: 'label', plural: 'labels' }}
            items={queue}
            renderItem={(item) => (
              <ResourceItem
                id={item.row.id}
                accessibilityLabel={`${item.row.title} - ${item.quantity} labels`}
              >
                <InlineStack gap="400" align="space-between" blockAlign="center">
                  <InlineStack gap="400" blockAlign="center">
                    <Thumbnail
                      source={item.row.image || '/img/product-placeholder.svg?v=2'}
                      alt={item.row.title}
                      size="small"
                    />
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text variant="bodyMd" fontWeight="semibold">
                          {item.row.title}
                        </Text>
                        {item.row.variantTitle && (
                          <Badge tone="info">{item.row.variantTitle}</Badge>
                        )}
                      </InlineStack>
                      <InlineStack gap="200">
                        {item.row.sku && (
                          <Text variant="bodySm" tone="subdued">
                            SKU: {item.row.sku}
                          </Text>
                        )}
                        {item.row.barcode ? (
                          <Badge>{item.row.barcode}</Badge>
                        ) : (
                          <Badge tone="attention">No barcode</Badge>
                        )}
                      </InlineStack>
                    </BlockStack>
                  </InlineStack>

                  <InlineStack gap="300" blockAlign="center">
                    <div style={{ width: '80px' }}>
                      <TextField
                        label="Quantity"
                        labelHidden
                        type="number"
                        min={1}
                        max={100}
                        value={item.quantity.toString()}
                        onChange={(value) =>
                          onUpdateQuantity(item.row.id, Math.max(1, parseInt(value) || 1))
                        }
                        autoComplete="off"
                      />
                    </div>
                    <Button
                      icon={DeleteIcon}
                      tone="critical"
                      onClick={() => onRemove(item.row.id)}
                      accessibilityLabel="Remove from queue"
                    />
                  </InlineStack>
                </InlineStack>
              </ResourceItem>
            )}
          />
        </Card>

        {/* Summary */}
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <InlineStack gap="400" align="space-between">
                <Text variant="bodyMd">Variants in queue</Text>
                <Text variant="bodyMd" fontWeight="semibold">{queue.length}</Text>
              </InlineStack>
              <InlineStack gap="400" align="space-between">
                <Text variant="bodyMd">Total labels</Text>
                <Text variant="bodyMd" fontWeight="semibold">{totalLabels}</Text>
              </InlineStack>
              <Divider />
              <InlineStack gap="400" align="space-between">
                <BlockStack gap="100">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {selectedPresetInfo?.name || 'Custom Labels'}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedPresetInfo?.printerType === 'thermal'
                      ? 'Thermal Printer'
                      : 'Laser/Inkjet'}{' '}
                    • {printFormat.toUpperCase()}
                  </Text>
                </BlockStack>
                <InlineStack gap="300">
                  <Button
                    icon={ViewIcon}
                    onClick={onPreview}
                    disabled={printFormat !== 'pdf'}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="primary"
                    icon={PrintIcon}
                    size="large"
                    disabled={needsSubscription}
                    loading={printing}
                    onClick={onPrint}
                  >
                    Print {totalLabels} Label{totalLabels !== 1 ? 's' : ''}
                  </Button>
                </InlineStack>
              </InlineStack>
            </BlockStack>
          </Box>
        </Card>

        {/* Free Demo Note */}
        {!subscription?.active && !isBulk && (
          <Banner tone="info">
            <p>
              <strong>Free demo:</strong> your first label print is free to test the
              quality. Subscribe for $9/month to unlock bulk printing and all
              features.
            </p>
          </Banner>
        )}
      </BlockStack>
    </Box>
  );
}

export default PrintQueue;
